import { LayoutGroup, motion } from 'framer-motion';
import { isHotkey } from 'is-hotkey';
import React, { useCallback, useEffect } from 'react';
import { styled } from 'styled-components';
import { v4 } from 'uuid';

import {
  boundaryRegex,
  searchBarFilterConfigs,
  searchBarMotionLayoutIdProps,
  searchBarMotionLayoutIds,
} from '../searchbar.config';
import type {
  SearchBarCommandCreatedInfo,
  SearchBarFilter,
  SearchBarFilterType,
  SearchBarValue,
} from '@fabric-app/business/search/searchbar.types';
import AddFilterDropdown from './AddFilterDropdown';
import SearchInput from './SearchInput';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';

import { SearchBarButton } from './SearchbarButton';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import SearchImage from './SearchImage';
import AddImageButton from './AddImageButton';
import { useDebouncedCallback } from '@fabric-app/hooks/useDebouncedCallback';
import { IconNavigational24Cross, IconSearch } from '@fabric-app/icons/svg';
import { SelectColorButton } from './SelectColorButton';
import { isSearchBarValueEmpty } from '@fabric-app/business/search/utils/isSearchBarValueEmpty';
import { SearchSimilarResource } from './SearchSimilarResource';
import { windowBlur } from '@fabric-app/business/utils/windowBlur';
const SearchBarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;

  width: min-content;
  min-width: 80px;
  width: 1px; // without this the container doesn't shrink
  height: 48px;
  position: relative;
  z-index: 1;
  gap: 4px;
  padding-right: 4px;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 21px; /* 140% */
`;

const SearchBarBg = styled(motion.div)`
  position: absolute;
  z-index: -1;
  width: 100%;
  height: 100%;

  border-radius: 14px;
  border: 1px solid transparent;
  background: ${cssVar['color-bg-section']};

  ${SearchBarContainer}:focus-within & {
    /* box-shadow: var(--shadow-spread-large) hsla(${cssVar['color-app-primary-hsl']}, 0.14); */
  }
`;

const SearchBarListbox = styled(motion.div)`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
  flex-grow: 1;
  height: 48px;
  cursor: text;
  position: relative;
`;

const SearchIconWrapper = styled(motion.span)`
  margin-left: 16px;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${cssVar['color-text-primary']};

  ${mediaMobile} {
    width: 18px;
    height: 18px;
    margin-left: 12px;
    margin-right: 0px;
  }
`;

const StyledScrollArea = styled(ScrollArea.Default)`
  flex-grow: 1;

  --scrollbar-size: 8px;
  --scrollbar-thumb-color: rgba(0, 0, 0, 0.3);
  --scrollbar-bg-color: transparent;
`;

type SearchBarProps = {
  value: SearchBarValue;
  setValue: React.Dispatch<React.SetStateAction<SearchBarValue>>;

  onBlur?: () => void;
  autoFocus?: boolean;
  isLoading?: boolean;
  listBoxRef: React.MutableRefObject<HTMLDivElement | null>;
};

export type SearchBarRef = {
  focus: () => void;
  blur: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
};

const SearchBar = React.forwardRef<SearchBarRef, SearchBarProps>(
  ({ value, setValue, onBlur, autoFocus, isLoading, listBoxRef }, ref) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const onClickListBox = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        // Focus the input when clicking the listbox
        // to prevent the listbox from stealing focus
        // from the input when clicking on the listbox
        // while the input is focused
        e.preventDefault();
        e.stopPropagation();

        const input = e.currentTarget.querySelector('[role="input"]') as HTMLInputElement;
        if (!input) return;
        // assuming the input is the first child of the listbox so we can only click to the right of the input
        // we move the cursor to the end of the input
        input.setSelectionRange(input.value.length, input.value.length);
        input.focus();
      }
    };

    const onChangeSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget) {
        const value = e.currentTarget.value;
        setValue((prev) => {
          return {
            ...prev,
            query: value,
          };
        });
      }
    };

    const lastCommandCreated = React.useRef<SearchBarCommandCreatedInfo | null>(null);

    const onAddNewFilter = (filterType: SearchBarFilterType) => {
      setValue((prevValue) => {
        const newFilter: SearchBarFilter = {
          $key: v4(),
          type: filterType,
          value: null,
        };
        return {
          ...prevValue,
          filters: [...prevValue.filters, newFilter],
        };
      });
    };

    const _onSelectColor = useCallback(
      (color: string | undefined) => {
        setValue((prev) => {
          return {
            ...prev,
            color,
          };
        });
      },
      [setValue],
    );
    const onSelectColor = useDebouncedCallback(_onSelectColor, 100);

    const onSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isHotkey('space', e)) return;

      // Get the text before the cursor
      const cursorStart = e.currentTarget.selectionStart;
      const cursorEnd = e.currentTarget.selectionEnd;

      if (!cursorStart || cursorStart !== cursorEnd) return;

      const textBeforeCursor = e.currentTarget.value.slice(0, cursorStart);

      for (const [type, filter] of Object.entries(searchBarFilterConfigs)) {
        const regex = new RegExp(boundaryRegex.source + filter.commandRegex.source, 'g');

        const match = regex.exec(textBeforeCursor);

        if (match) {
          // Prevent the space character from being inserted
          e.preventDefault();

          const newValue =
            e.currentTarget.value.slice(0, cursorStart - match[0].length) +
            e.currentTarget.value.slice(cursorStart);

          const $key = v4();

          setValue((val) => ({
            ...val,
            query: newValue,
            filters: [
              ...val.filters,
              {
                $key,
                type: type as SearchBarFilterType,
                value: null,
              },
            ],
          }));
          lastCommandCreated.current = {
            $key,
            match: match[0] + ' ',
            position: cursorStart - match[0].length,
          };

          // remove the command from the input
          e.currentTarget.blur();

          break;
        }
      }
    };

    const onSearchBarKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
      if (isHotkey('enter', e) && isMobileView) {
        e.preventDefault();
        e.stopPropagation();
        windowBlur();
        return;
      }
      if (!isHotkey('esc', e) || !e.currentTarget.contains(document.activeElement)) return;
      if (e.target instanceof HTMLInputElement) {
        return;
      }

      e.stopPropagation();
      if (e.target instanceof HTMLElement) e.target.blur();
    };

    const isEmpty = isSearchBarValueEmpty(value);
    const onClear = () => {
      setValue({ query: '', filters: [], color: undefined });
      inputRef.current?.focus();
    };

    React.useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
      blur: () => {
        inputRef.current?.focus();
        setTimeout(() => {
          inputRef.current?.blur();
        }, 0);
      },
      inputRef,
    }));

    useEffect(() => {
      if (!onBlur) return;

      let debounce: number | null = null;

      const onCheck = () => {
        if (
          containerRef.current?.contains(document.activeElement) ||
          listBoxRef.current?.contains(document.activeElement) ||
          !!document.activeElement?.closest('[data-search-bar-filters-container]') ||
          !!document.activeElement?.closest('[data-radix-popper-content-wrapper]')
        )
          return;
        onBlur();
      };

      const onDebounce = (e: Event) => {
        if (e.defaultPrevented) return;

        if (debounce) clearTimeout(debounce);
        debounce = window.setTimeout(onCheck, 30);
      };

      document.addEventListener('focus', onDebounce, true);
      document.addEventListener('pointerdown', onDebounce, true);

      return () => {
        if (debounce) clearTimeout(debounce);

        document.removeEventListener('focus', onDebounce, true);
        document.removeEventListener('pointerdown', onDebounce, true);
      };
    }, [listBoxRef, onBlur]);

    const { isMobileView } = useResponsive();

    const onSetImageUrl = (url: string) => {
      setValue((val) => ({ ...val, imageUrl: url, similarToResource: null, query: '' }));
    };

    const onRemoveImageUrl = () => {
      setValue((val) => ({ ...val, imageUrl: undefined }));
    };
    const onRemoveSimilarToResource = () => {
      setValue((val) => ({ ...val, similarToResource: undefined }));
    };

    return (
      <SearchBarContainer onKeyDown={onSearchBarKeyDown} ref={containerRef}>
        <LayoutGroup id={searchBarMotionLayoutIds.group}>
          <SearchBarBg {...searchBarMotionLayoutIdProps.bg} />
          <SearchIconWrapper {...searchBarMotionLayoutIdProps.icon}>
            {isLoading ? <Spinner size={18} /> : <IconSearch style={{ width: 24, height: 24 }} />}
          </SearchIconWrapper>

          <StyledScrollArea orientation="horizontal">
            <SearchBarListbox
              role="listbox"
              onClick={onClickListBox}
              tabIndex={-1}
              ref={listBoxRef}
            >
              {value.imageUrl && <SearchImage src={value.imageUrl} onRemove={onRemoveImageUrl} />}
              {value.similarToResource && (
                <SearchSimilarResource
                  resource={value.similarToResource}
                  onRemove={onRemoveSimilarToResource}
                />
              )}

              {!value.imageUrl && !value.similarToResource && (
                <SearchInput
                  {...searchBarMotionLayoutIdProps.input}
                  placeholder={isMobileView ? 'Search with AI' : 'What are you looking for?'}
                  role="input"
                  onChange={onChangeSearchInput}
                  onKeyDown={onSearchInputKeyDown}
                  value={value.query}
                  ref={inputRef}
                  autoFocus={autoFocus}
                  autoCapitalize="off"
                />
              )}
            </SearchBarListbox>
          </StyledScrollArea>

          <SelectColorButton color={value.color} onChange={onSelectColor} />
          <AddImageButton key="add-image" onSetImageURL={onSetImageUrl} />

          <AddFilterDropdown key="add-filter" onFilterSelect={onAddNewFilter} />
          {!isEmpty && (
            <SearchBarButton
              {...searchBarMotionLayoutIdProps.clearButton}
              key="close"
              onClick={onClear}
            >
              <IconNavigational24Cross />
            </SearchBarButton>
          )}
        </LayoutGroup>
      </SearchBarContainer>
    );
  },
);

SearchBar.displayName = 'SearchBar';

export default SearchBar;
